import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useSelectionData, resolveMetricValue } from '../../services/useSelectionData';
import { formatMetricValue } from '../../services/metricRegistry';
import { getNarrativeScope, formatPlace } from '../../services/metricNarratives';
// Matches CompareChart's heading exactly, mt-2 included. That mt-2 is for a
// heading that follows a seam rather than one that opens a box, and following a
// seam is what this heading does now: the pie is a joined .panel-flow
// continuation, so .panel-flow > * draws the divider immediately above it.
// It matched SupervisionRatio's heading instead - no mt-2 - for as long as the
// pie was a detached .panel-flow-end card opening a box of its own.
const SECTION_LABEL_CLASS = 'd-block text-uppercase text-black-60 fw-semibold mb-2 h6 mt-2';
/**
 * The only category this card belongs under, and the only metric it draws.
 *
 * Typed as CategoryId / MetricId so a rename in the registry is a compile error
 * here rather than a card that silently never renders again — the same guard
 * SupervisionRatio puts on RATIO_CATEGORY.
 */
const PIE_CATEGORY = 'female';
const FEMALE = 'female';
/**
 * Where the pie is allowed to draw.
 *
 * County is excluded because the metric has no county field (see its descriptor
 * in metricRegistry.ts — `stateField` only). That exclusion is not merely
 * cosmetic: at County level with nothing drilled into, resolveMetricValue falls
 * back to the state row, so without this gate the card would render the United
 * States' share while the map showed counties. With a county actually selected
 * the read returns null and the card drops out on its own, but the fallback
 * case needs saying explicitly.
 *
 * National IS included, unlike the bar chart this replaces. CompareChart
 * withheld Female at national scope because a single bar with no comparison
 * series says nothing; a pie has its comparison built in — the slice and its
 * remainder — so the national row is worth drawing.
 */
const PIE_SCOPES = ['national', 'state'];
// Same two hexes CompareChart uses for its selected/comparison series, and the
// same pair .hv-legend pins its swatches to in dashboard.css. The legend below
// is that shared markup, so these cannot drift from the chips beneath them
// without the chips being wrong too.
const FEMALE_COLOR = '#145A9E';
const REMAINDER_COLOR = '#C9D2DB';
// One style, because only the female slice is labeled. Its color is read
// against the SLICE rather than the card — the label sits inside the wedge — so
// this is not the --mapdashboard-scope-heading token the other sidebar cards
// use; white is what clears the dark blue beneath it.
//
// textOutline is off: Highcharts halos contrasting text by default, and on a
// flat fill that reads as a smudge rather than as separation.
const FEMALE_LABEL_STYLE = {
    fontSize: '12px', fontWeight: '700', color: '#FFFFFF', textOutline: 'none'
};
/** One decimal, the precision the underlying percentages arrive at. */
function roundShare(n) {
    return Math.round(n * 10) / 10;
}
/**
 * The female percentage as a number the pie can plot, or null.
 *
 * Null covers three cases that must not reach the chart: an absent value, a
 * suppression sentinel ('<5' / 'Missing' — strings, which formatMetricValue
 * passes through as prose and Highcharts would plot as zero), and a figure
 * outside 0-100, which would make the remainder slice negative. The last is a
 * data fault rather than a display case, so the card withholds itself instead
 * of drawing a pie whose two slices do not sum to the whole it claims.
 */
function toShare(raw) {
    if (typeof raw !== 'number' || !Number.isFinite(raw))
        return null;
    if (raw < 0 || raw > 100)
        return null;
    return roundShare(raw);
}
/**
 * Composition of the home-visiting workforce by the one demographic field the
 * Year 2 data carries: the share identifying as female, against the remainder.
 *
 * Renders only under the Female category — see PIE_CATEGORY — so like the
 * supervision ratio it always elaborates on the metric in view rather than
 * standing beside an unrelated one. It replaces the comparison chart for this
 * category: Female holds exactly one metric, so CompareChart drew a chart of a
 * single bar, which is a worse rendering of the same number than a part-of-whole
 * form. That withdrawal is made in CompareChart's own CHART_ALLOWLIST, not here.
 *
 * The remainder slice is derived (100 − female), not read: no "not female"
 * field exists, and none is implied. It is labeled "Not female" rather than
 * "Male" for that reason — the source measures one category and its complement,
 * which is not the same statement as a gender breakdown.
 *
 * The tooltip stays off, because every value the chart holds is already printed
 * on it. That is also what keeps it honest on paper, where interaction is not
 * available to reveal anything held back.
 *
 * Hover and selection are on: a click pulls a slice out, a second click puts it
 * back (see plotOptions.pie). That gesture is decorative — it discloses nothing
 * the labels and the sentence below do not already state — and that is what
 * lets the SVG stay hidden from assistive tech with no keyboard equivalent
 * offered for it. The accessibility module is not in this bundle, so points
 * take no tabindex and there is no keyboard route to a slice. Give the click a
 * job that changes what the page reports and that stops being true: it would
 * then be a mouse-only control conveying information, and would need a
 * focusable equivalent outside the aria-hidden wrapper.
 *
 * The SVG is hidden from assistive tech and replaced by the sentence below it,
 * which states both shares and the geography they describe in one pass instead
 * of as a scatter of unattributed label fragments.
 */
const FemaleSharePie = ({ dashboardState }) => {
    const { countyData, stateData, isLoading } = useSelectionData(dashboardState);
    const share = toShare(resolveMetricValue(dashboardState, countyData, stateData, FEMALE).raw);
    const place = formatPlace(dashboardState);
    const options = React.useMemo(() => {
        if (share === null)
            return null;
        const remainder = roundShare(100 - share);
        // A zero remainder is dropped rather than plotted: a 100% female
        // workforce is one whole slice, and a second point carrying nothing
        // would still take a legend chip below naming a category with no
        // members.
        const points = [
            {
                name: 'Female',
                y: share,
                color: FEMALE_COLOR,
                dataLabels: { style: FEMALE_LABEL_STYLE },
                custom: { formatted: formatMetricValue(FEMALE, share) }
            },
            ...(remainder > 0 ? [{
                    name: 'Not Female',
                    y: remainder,
                    color: REMAINDER_COLOR,
                    // Unlabeled on the chart. The female share is the figure this
                    // card exists to report, and printing its complement beside it
                    // states the same fact twice — the two are one subtraction
                    // apart. The wedge still carries the proportion visually, and
                    // the legend below names it.
                    //
                    // No `custom` either: the formatter below reads that, and with
                    // no label to run for it would be data kept for nothing.
                    dataLabels: { enabled: false }
                }] : [])
        ];
        return {
            chart: {
                type: 'pie', backgroundColor: 'transparent', height: 210,
                // Zero on every side. Spacing is subtracted from the plot area
                // before the pie is sized, so any of it here comes straight off
                // the diameter — it is padding around the circle by another
                // name.
                spacing: [0, 0, 0, 0], style: { fontFamily: 'inherit' },
                // Selecting a different state replaces both slices outright.
                // Tweening one composition into another animates through
                // splits that describe no geography at all, and the labels
                // chase the moving wedges while they do it. Same reasoning as
                // CompareChart, and the same pairing: this flag covers the
                // update, plotOptions.pie.animation covers the first draw.
                animation: false
            },
            title: { text: undefined },
            plotOptions: {
                pie: {
                    animation: false,
                    borderWidth: 0,
                    // The circle takes the whole plot area — with spacing at
                    // zero above, the diameter is the full 210px height.
                    //
                    // This is only available because the labels moved INSIDE
                    // the slices. A label drawn outside has to be reserved for
                    // out of the same box, which is what the 72% here used to
                    // be paying for: at any size near 100% the percentages
                    // clipped at the chart edge. Inside placement takes that
                    // reservation to zero, so the two settings move together —
                    // putting the labels back outside means giving the size
                    // back as well.
                    size: '100%',
                    dataLabels: {
                        enabled: true,
                        // Inward from the slice edge. Deep enough that the
                        // label clears the rim on the narrow slice rather than
                        // straddling it.
                        distance: -28,
                        formatter: function () {
                            var _a, _b, _c, _d;
                            return (_d = (_c = (_b = (_a = this.point) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.custom) === null || _c === void 0 ? void 0 : _c.formatted) !== null && _d !== void 0 ? _d : '';
                        }
                    },
                    // ============ Clickable slices ============
                    //
                    // enableMouseTracking is the switch. At false the series is
                    // detached from Pointer altogether — no mouseOver, no
                    // tooltip and, the part that reads as a broken chart, no
                    // click. `animation` has nothing to do with it: it decides
                    // how the slices are drawn, never whether they are tracked.
                    //
                    // allowPointSelect is what gives the click something to do.
                    // Highcharts has no default click behavior for a pie — the
                    // slice-out IS the select state — so a tracked slice with
                    // this off still sits still when clicked. Clicking again
                    // deselects and the slice returns.
                    //
                    // states.hover is no longer zeroed, so the slice under the
                    // cursor brightens. The halo is drawn from the point's own
                    // state config, which is why it had to be disabled here
                    // alongside tracking and has to come back alongside it.
                    //
                    // slicedOffset is deliberately NOT set. It defaults to 10
                    // and getCenter reserves 2x it as slicing room whether or
                    // not anything can be selected, so that room is already
                    // being paid for: the circle draws at 190px inside the
                    // 210px box today. Naming a smaller offset here would grow
                    // the pie and move it out from under the size:'100%' and
                    // distance:-28 measurements above. Leaving it alone means
                    // selection changes no geometry at all.
                    enableMouseTracking: true,
                    allowPointSelect: true,
                    cursor: 'pointer'
                }
            },
            tooltip: { enabled: false },
            legend: { enabled: false },
            credits: { enabled: false },
            exporting: { enabled: false },
            series: [{
                    type: 'pie',
                    name: 'Home visitors',
                    data: points
                }]
        };
    }, [share]);
    // Both gates sit below every hook so the hook order stays stable across
    // renders when a selection change flips either one — the arrangement
    // CompareChart and SupervisionRatio both use.
    if (dashboardState.selectedCategoryId !== PIE_CATEGORY)
        return null;
    if (!PIE_SCOPES.includes(getNarrativeScope(dashboardState)))
        return null;
    // No value, nothing to divide. Rendering no card is right rather than a
    // "coming soon" line: unlike CompareChart, which stands in for a whole
    // category of metrics that may arrive later, this card is one number and
    // its absence is already reported by the narrative above it.
    if (isLoading || options === null)
        return null;
    const remainder = roundShare(100 - share);
    const spoken = remainder > 0
        ? `In ${place}, ${formatMetricValue(FEMALE, share)} of home visitors identify as female and `
            + `${formatMetricValue(FEMALE, remainder)} do not.`
        : `In ${place}, all home visitors identify as female.`;
    return (_jsx("div", { className: "panel-flow", children: _jsxs("div", { className: "female-pie-block", children: [_jsx("h3", { className: SECTION_LABEL_CLASS, children: "Percentage of Female Home Visitors" }), _jsx("div", { "aria-hidden": "true", children: _jsx(HighchartsReact, { highcharts: Highcharts, options: options }) }), _jsx("p", { className: "visually-hidden", children: spoken }), _jsx("div", { className: "hv-legend", children: _jsxs("span", { className: "lg", children: [_jsx("i", { className: "s" }), "Female"] }) })] }) }));
};
export default FemaleSharePie;
